
import React, { useState, useEffect } from 'react';
import { db } from '../services/mockDb';
import { PACKAGES, UDO_PACKAGES } from '../services/pricingUtils';
import { OrderForm, Product, FormSection, SectionType, WebLead, LeadStatus, User } from '../types';

const getPackagesForAgent = (name?: string, email?: string) => {
  const isUdo = (name?.toLowerCase().includes('udo')) || (email?.toLowerCase() === 'abelchill000@gmail.com');
  if (isUdo) {
    return UDO_PACKAGES;
  }
  return PACKAGES;
};

const NIGERIA_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta',
  'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina',
  'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers',
  'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const SECTION_DEFAULTS: Record<SectionType, Partial<FormSection>> = {
  HEADER: { label: 'Order Your Magira Shots', content: 'Fresh organic health shots delivered to your doorstep.' },
  CONTACT: { label: 'Enter Shipping Info' },
  PRODUCTS: { 
    label: 'Choose Your Package',
    options: PACKAGES.map(p => ({ 
      label: p.label, 
      value: JSON.stringify({ qty: p.qty, price: p.price, label: p.label }), 
      price: p.price, 
      qty: p.qty 
    }))
  },
  ADDRESS: { label: 'Street Address', content: 'Full Delivery Address' },
  LOCATION: { label: 'Delivery State', content: 'Select State' },
  DELIVERY_INSTRUCTIONS: { label: 'Delivery Instructions', content: 'e.g. Leave at the gate, Call before arrival, etc.' },
  CUSTOM_TEXT: { label: 'Special Instructions', content: 'Delivery takes 24-48 hours.' },
  BENEFITS: { label: 'Why Choose Magira?', content: '100% Organic\nNo Preservatives\nInstant Energy\nRich in Vitamin C' },
  TESTIMONIALS: { label: 'Adebayo M.', content: '"The best ginger shot in Lagos. I feel energized all day!"' },
  FAQ: { label: 'Is it safe for kids?', content: 'Yes, our ginger shots are made from 100% natural ginger and lemon.' },
  IMAGE: { label: 'Banner Image', content: 'https://images.unsplash.com/photo-1622484211148-71629844865c?q=80&w=800' },
};

const FormBuilder: React.FC = () => {
  const user = db.getCurrentUser();
  const [forms, setForms] = useState<OrderForm[]>(db.getForms());
  const [users, setUsers] = useState<User[]>(user?.role === 'Admin' ? db.getUsers().filter(u => u.isApproved) : []);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState<Partial<OrderForm> | null>(null);
  const [previewForm, setPreviewForm] = useState<OrderForm | null>(null);
  const [showCodeModal, setShowCodeModal] = useState<OrderForm | null>(null);
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');

  const [previewData, setPreviewData] = useState<any>({
    customerName: '',
    phone: '',
    whatsapp: '',
    package: '',
    address: '',
    stateName: '',
    deliveryInstructions: ''
  });
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const [isSubmittingPreview, setIsSubmittingPreview] = useState(false);

  useEffect(() => {
    const unsub = db.subscribe(() => {
      setForms(db.getForms());
      if (user?.role === 'Admin') {
        setUsers(db.getUsers().filter(u => u.isApproved));
      }
    });
    return unsub;
  }, [user?.role]);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingForm || !user) return;

    const form: OrderForm = {
      id: editingForm.id || 'form-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
      title: editingForm.title || 'New Order Form',
      description: editingForm.description || '',
      productIds: editingForm.productIds || [],
      themeColor: editingForm.themeColor || '#10b981',
      createdAt: editingForm.createdAt || new Date().toISOString(),
      isActive: editingForm.isActive ?? true,
      sections: editingForm.sections || [],
      submitButtonText: editingForm.submitButtonText || 'Place Order Now',
      successMessage: editingForm.successMessage || 'Thank you! Your order request has been received.',
      thankYouUrl: editingForm.thankYouUrl || '',
      createdBy: editingForm.createdBy || user.name,
      assignedToNames: editingForm.assignedToNames || []
    };

    await db.saveForm(form);
    setForms([...db.getForms()]);
    setShowModal(false);
    setEditingForm(null);
  };

  const addSection = (type: SectionType) => {
    let defaults = { ...SECTION_DEFAULTS[type] };
    
    if (type === 'PRODUCTS') {
      const agentPackages = getPackagesForAgent(user?.name, user?.email);
      defaults.options = agentPackages.map(p => ({ 
        label: p.label, 
        value: JSON.stringify({ qty: p.qty, price: p.price, label: p.label }), 
        price: p.price, 
        qty: p.qty 
      }));
    }

    const newSection: FormSection = {
      id: 'sec-' + Math.random().toString(36).substr(2, 5),
      type,
      ...defaults
    };
    const currentSections = editingForm?.sections || [];
    setEditingForm({ ...editingForm, sections: [...currentSections, newSection] });
  };

  const removeSection = (id: string) => {
    const updated = (editingForm?.sections || []).filter(s => s.id !== id);
    setEditingForm({ ...editingForm, sections: updated });
  };

  const updateOption = (sectionIdx: number, optionIdx: number, field: string, value: any) => {
    const updated = [...(editingForm?.sections || [])];
    const section = updated[sectionIdx];
    if (!section.options) return;
    
    const newOptions = [...section.options];
    newOptions[optionIdx] = { ...newOptions[optionIdx], [field]: value };
    
    if (section.type === 'PRODUCTS') {
      const qty = field === 'qty' ? Number(value) : newOptions[optionIdx].qty;
      const price = field === 'price' ? Number(value) : newOptions[optionIdx].price;
      const label = field === 'label' ? value : newOptions[optionIdx].label;
      newOptions[optionIdx].value = JSON.stringify({ qty, price, label });
      newOptions[optionIdx].qty = qty;
      newOptions[optionIdx].price = price;
      newOptions[optionIdx].label = label;
    }
    
    section.options = newOptions;
    setEditingForm({ ...editingForm, sections: updated });
  };

  const addOption = (sectionIdx: number) => {
    const updated = [...(editingForm?.sections || [])];
    const section = updated[sectionIdx];
    const newOption = section.type === 'PRODUCTS' 
      ? { label: 'New Package', value: JSON.stringify({ qty: 1, price: 0, label: 'New Package' }), qty: 1, price: 0 }
      : { label: 'New Field', value: 'New Field' };
    
    section.options = [...(section.options || []), newOption];
    setEditingForm({ ...editingForm, sections: updated });
  };

  const removeOption = (sectionIdx: number, optionIdx: number) => {
    const updated = [...(editingForm?.sections || [])];
    const section = updated[sectionIdx];
    if (!section.options) return;
    
    section.options = section.options.filter((_, i) => i !== optionIdx);
    setEditingForm({ ...editingForm, sections: updated });
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const sections = [...(editingForm?.sections || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sections.length) return;
    
    [sections[index], sections[targetIndex]] = [sections[targetIndex], sections[index]];
    setEditingForm({ ...editingForm, sections });
  };

  const handlePreviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!previewForm || !user) return;

    // Custom Validation: Phone/WhatsApp must be 11 digits
    const phoneRegex = /^\d{11}$/;
    if (!phoneRegex.test(previewData.phone)) {
      alert("Phone number must be exactly 11 digits.");
      return;
    }
    if (previewData.whatsapp && !phoneRegex.test(previewData.whatsapp)) {
      alert("WhatsApp number must be exactly 11 digits.");
      return;
    }

    // Custom Validation: Address must be at least 3 words
    const address = previewData.address || '';
    const wordCount = address.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    if (wordCount < 3) {
      alert("Please provide a more detailed delivery address (at least 3 words).");
      return;
    }

    setIsSubmittingPreview(true);

    let pkg = { qty: 1, price: 0, label: '' };
    try { 
      const parsed = JSON.parse(previewData.package);
      pkg = { 
        qty: parsed.qty || 1, 
        price: parsed.price || 0, 
        label: parsed.label || '' 
      };
    } catch(err) {}

    const lead: WebLead = {
      id: 'L-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
      formId: previewForm.id,
      customerName: previewData.customerName,
      phone: previewData.phone,
      whatsapp: previewData.whatsapp,
      address: previewData.address,
      stateName: previewData.stateName,
      deliveryInstructions: previewData.deliveryInstructions,
      items: [{ productId: 'GINGER-SHOT-500ML', quantity: pkg.qty, priceAtCapture: pkg.price, packageLabel: pkg.label }],
      status: LeadStatus.NEW,
      notes: `Captured from LIVE PREVIEW by ${user.name}`,
      createdAt: new Date().toISOString(),
      agentName: user.name
    };

    await db.createLead(lead);

    setTimeout(() => {
      setIsSubmittingPreview(false);
      setPreviewSubmitted(true);
    }, 1500);
  };

  const getEmbedCode = (form: OrderForm) => {
    const agentName = (form.assignedToNames && form.assignedToNames.length > 0) ? form.assignedToNames[0] : form.createdBy;
    
    const productsSection = form.sections.find(s => s.type === 'PRODUCTS');

    const agentPackages = getPackagesForAgent(agentName);
    const packageOptions = (productsSection?.options || agentPackages.map(p => ({ label: p.label, value: JSON.stringify({ qty: p.qty, price: p.price, label: p.label }), price: p.price, qty: p.qty }))).map(opt => {
      const value = opt.value.includes('"label":') ? opt.value : JSON.stringify({ qty: opt.qty, price: opt.price, label: opt.label });
      return `<option value='${value}'>${opt.label}</option>`;
    }).join('\n            ');

    const sectionsHtml = form.sections.map(sec => {
      switch (sec.type) {
        case 'HEADER':
          return `<div style="background-color: ${form.themeColor}; padding: 32px 24px; color: white; text-align: center;"><h2 style="margin: 0; font-size: 24px; font-weight: 900;">${sec.label || form.title}</h2><p style="margin: 12px 0 0 0; font-size: 15px; opacity: 0.9; line-height: 1.5;">${sec.content || form.description}</p></div>`;
        case 'IMAGE':
          return `<div style="width: 100%;"><img src="${sec.content}" style="width: 100%; display: block; height: auto;" alt="Magira Image"></div>`;
        case 'CONTACT':
          return `<div style="padding: 0 24px;"><label style="display: block; font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">${sec.label}</label><input type="text" name="customerName" placeholder="Full Name" required style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; margin-bottom: 12px; font-size: 14px;"><input type="tel" name="phone" placeholder="Phone Number (Call) - 11 Digits" required pattern="\\d{11}" title="Please enter exactly 11 digits" style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; margin-bottom: 12px; font-size: 14px;"><input type="tel" name="whatsapp" placeholder="WhatsApp Number - 11 Digits" required pattern="\\d{11}" title="Please enter exactly 11 digits" style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; font-size: 14px;"></div>`;
        case 'PRODUCTS':
          return `<div style="padding: 0 24px;"><label style="display: block; font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">${sec.label}</label><select name="packageData" required style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; background: white; font-size: 14px; font-weight: 600;"><option value="">-- Choose Ginger Shot Package --</option>${packageOptions}</select></div>`;
        case 'ADDRESS':
          return `<div style="padding: 0 24px;"><label style="display: block; font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">${sec.label}</label><textarea name="address" required rows="2" placeholder="${sec.content || 'Full Delivery Address'}" style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; font-family: sans-serif; font-size: 14px;"></textarea></div>`;
        case 'LOCATION':
          const stateOptions = NIGERIA_STATES.map(s => `<option value="${s}">${s}</option>`).join('');
          return `<div style="padding: 0 24px;"><label style="display: block; font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">${sec.label}</label><select name="stateName" required style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; background: white; font-size: 14px; font-weight: 600;"><option value="">-- Select Delivery State --</option>${stateOptions}</select></div>`;
        case 'DELIVERY_INSTRUCTIONS':
          return `<div style="padding: 0 24px;"><label style="display: block; font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px;">${sec.label}</label><textarea name="deliveryInstructions" required rows="2" placeholder="${sec.content || 'Add specific instructions for the delivery agent...'}" style="width: 100%; padding: 14px; border: 1.5px solid #f1f5f9; border-radius: 12px; box-sizing: border-box; font-family: sans-serif; font-size: 14px;"></textarea></div>`;
        case 'BENEFITS':
          const benefits = (sec.content || '').split('\n').map(b => `<li style="margin-bottom: 10px; display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: #475569;"><span style="color: ${form.themeColor}; font-weight: bold;">✓</span> <span>${b.trim()}</span></li>`).join('');
          return `<div style="padding: 24px; background: #f8fafc; border-radius: 16px; margin: 0 24px;"><h3 style="margin: 0 0 16px 0; font-size: 16px; font-weight: 800; color: #1e293b;">${sec.label}</h3><ul style="list-style: none; padding: 0; margin: 0;">${benefits}</ul></div>`;
        case 'TESTIMONIALS':
          return `<div style="padding: 24px; border-radius: 16px; border: 2px solid #f1f5f9; margin: 0 24px; background: white;"><p style="margin: 0; font-style: italic; color: #475569; line-height: 1.6; font-size: 15px;">${sec.content}</p><p style="margin: 12px 0 0 0; font-weight: 800; font-size: 13px; color: #1e293b;">— ${sec.label}</p></div>`;
        case 'FAQ':
          return `<div style="padding: 0 24px;"><h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 800; color: #1e293b;">Q: ${sec.label}</h3><p style="margin: 0; font-size: 14px; color: #64748b; line-height: 1.6;">A: ${sec.content}</p></div>`;
        case 'CUSTOM_TEXT':
          return `<div style="padding: 0 24px;"><h4 style="margin: 0; font-size: 14px; font-weight: 800; color: #334155;">${sec.label}</h4><p style="margin: 4px 0 0 0; font-size: 13px; color: #64748b; line-height: 1.6;">${sec.content}</p></div>`;
        default: return '';
      }
    }).join('\n');

    return `<!-- Magira Lead Recovery Logic: Generated for ${agentName} -->
<div id="magira-container-${form.id}" style="font-family: 'Inter', sans-serif; max-width: 480px; margin: 20px auto; border: 1px solid #f1f5f9; border-radius: 24px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08); background: white;">
  <form id="magira-form-${form.id}" style="display: flex; flex-direction: column; gap: 24px; padding-bottom: 32px;">
    ${sectionsHtml}
    
    <div style="padding: 8px 24px 0 24px;">
      <button type="submit" style="width: 100%; background-color: ${form.themeColor}; color: white; border: none; padding: 18px; border-radius: 16px; font-weight: 900; font-size: 16px; cursor: pointer; transition: all 0.2s; box-shadow: 0 10px 15px -3px ${form.themeColor}33;">
        ${form.submitButtonText}
      </button>
      <p style="text-align: center; margin: 16px 0 0 0; font-size: 10px; color: #cbd5e1; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;">Secure Fulfillment via Magira CRM</p>
    </div>
  </form>
</div>

<script>
(function() {
  const formId = "${form.id}";
  const agentName = "${agentName}";
  const apiKey = "AIzaSyDjIST5wP--TJhSxmbDqvgTSHUUFeMJVwE";
  const projectId = "magiracrm";
  const sessionId = 'SESS-' + Math.random().toString(36).substr(2, 9).toUpperCase();
  
  let debounceTimer;
  const formEl = document.getElementById('magira-form-' + formId);

  // Real-time Field Capture for Abandoned Carts
  formEl.addEventListener('input', function(e) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const formData = new FormData(formEl);
      const rawData = Object.fromEntries(formData.entries());
      
      let pkg = { qty: 1, price: 0, label: '' };
      try { pkg = JSON.parse(rawData.packageData); } catch(err) {}

      const payload = {
        id: sessionId,
        formId: formId,
        customerName: rawData.customerName || '',
        phone: rawData.phone || '',
        whatsapp: rawData.whatsapp || '',
        address: rawData.address || '',
        stateName: rawData.stateName || '',
        deliveryInstructions: rawData.deliveryInstructions || '',
        agentName: agentName,
        status: 'abandoned',
        lastUpdatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(), // Only set on first write
        pageUrl: window.location.href,
        items: [{ productId: 'GINGER-SHOT-500ML', quantity: pkg.qty, priceAtCapture: pkg.price, packageLabel: pkg.label }]
      };

      // Push partial data to Firestore abandoned_carts
      fetch(\`https://firestore.googleapis.com/v1/projects/\${projectId}/databases/(default)/documents/abandoned_carts/\${sessionId}?key=\${apiKey}\`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: {
            id: { stringValue: payload.id },
            formId: { stringValue: payload.formId },
            customerName: { stringValue: payload.customerName },
            phone: { stringValue: payload.phone },
            whatsapp: { stringValue: payload.whatsapp },
            address: { stringValue: payload.address },
            stateName: { stringValue: payload.stateName },
            deliveryInstructions: { stringValue: payload.deliveryInstructions },
            agentName: { stringValue: payload.agentName },
            status: { stringValue: payload.status },
            lastUpdatedAt: { stringValue: payload.lastUpdatedAt },
            createdAt: { stringValue: payload.createdAt },
            pageUrl: { stringValue: payload.pageUrl },
            items: {
              arrayValue: {
                values: payload.items.map(i => ({
                  mapValue: { fields: { productId: { stringValue: i.productId }, quantity: { integerValue: i.quantity.toString() }, priceAtCapture: { doubleValue: i.priceAtCapture || 0 }, packageLabel: { stringValue: i.packageLabel || '' } } }
                }))
              }
            }
          }
        })
      }).catch(err => console.debug('Recovery update failed'));
    }, 1500);
  });

  // Successful Lead Submission
  formEl.addEventListener('submit', async function(e) {
    e.preventDefault();
    
    const formData = new FormData(this);
    const rawData = Object.fromEntries(formData.entries());
    
    // Custom Validation: Address must be at least 3 words
    const address = rawData.address || '';
    const wordCount = address.trim().split(/\\s+/).filter(word => word.length > 0).length;
    if (wordCount < 3) {
      alert('Please provide a more detailed delivery address (at least 3 words).');
      return;
    }

    const btn = this.querySelector('button');
    const originalText = btn.innerText;
    
    btn.disabled = true;
    btn.innerText = 'Transmitting Order...';
    
    let pkg = { qty: 1, price: 0, label: '' };
    try { pkg = JSON.parse(rawData.packageData); } catch(err) {}

    const leadId = 'L-' + Math.random().toString(36).substr(2, 6).toUpperCase();
    const payload = {
      id: leadId,
      formId: formId,
      customerName: rawData.customerName,
      phone: rawData.phone,
      whatsapp: rawData.whatsapp || '',
      address: rawData.address,
      stateName: rawData.stateName || '',
      deliveryInstructions: rawData.deliveryInstructions || '',
      status: 'New Lead',
      notes: 'Captured via Landing Page by ' + agentName,
      createdAt: new Date().toISOString(),
      agentName: agentName,
      items: [{ productId: 'GINGER-SHOT-500ML', quantity: pkg.qty, priceAtCapture: pkg.price, packageLabel: pkg.label }]
    };

    try {
      // 1. Send the full lead
      const response = await fetch(\`https://firestore.googleapis.com/v1/projects/\${projectId}/databases/(default)/documents/leads?documentId=\${leadId}&key=\${apiKey}\`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: {
            id: { stringValue: payload.id },
            formId: { stringValue: payload.formId },
            customerName: { stringValue: payload.customerName },
            phone: { stringValue: payload.phone },
            whatsapp: { stringValue: payload.whatsapp },
            address: { stringValue: payload.address },
            stateName: { stringValue: payload.stateName },
            deliveryInstructions: { stringValue: payload.deliveryInstructions },
            status: { stringValue: payload.status },
            notes: { stringValue: payload.notes },
            createdAt: { stringValue: payload.createdAt },
            agentName: { stringValue: payload.agentName },
            items: {
              arrayValue: {
                values: payload.items.map(i => ({
                  mapValue: {
                    fields: {
                      productId: { stringValue: i.productId },
                      quantity: { integerValue: i.quantity.toString() },
                      priceAtCapture: { doubleValue: i.priceAtCapture || 0 },
                      packageLabel: { stringValue: i.packageLabel || '' }
                    }
                  }
                }))
              }
            }
          }
        })
      });

      if (!response.ok) throw new Error('Transmission Failed');

      // 2. Mark abandoned cart as converted
      fetch(\`https://firestore.googleapis.com/v1/projects/\${projectId}/databases/(default)/documents/abandoned_carts/\${sessionId}?updateMask.fieldPaths=status&key=\${apiKey}\`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { status: { stringValue: 'converted' } } })
      });

      const thankYouUrl = "${form.thankYouUrl || ''}";
      if (thankYouUrl) {
        window.location.href = thankYouUrl;
      } else {
        document.getElementById('magira-container-' + formId).innerHTML = \`
          <div style="padding: 80px 40px; text-align: center; color: #1e293b;">
            <div style="font-size: 72px; margin-bottom: 24px;">✅</div>
            <h3 style="margin: 0 0 12px 0; font-size: 24px; font-weight: 900;">Order Captured!</h3>
            <p style="color: #64748b; font-size: 15px; line-height: 1.7;">\${agentName} has received your order and will contact you shortly.</p>
          </div>
        \`;
      }
    } catch (error) {
      console.error('Magira Error:', error);
      alert('Connection error. Please try again.');
      btn.disabled = false;
      btn.innerText = originalText;
    }
  });
})();
</script>`;
  };

  const openPreview = (form: OrderForm) => {
    setPreviewForm(form);
    setPreviewSubmitted(false);
    setPreviewData({
      customerName: '',
      phone: '',
      whatsapp: '',
      package: '',
      address: '',
      deliveryInstructions: ''
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Landing Page Creator</h1>
          <p className="text-slate-500 text-sm font-medium">Build custom sales pages to capture your own leads.</p>
        </div>
        <button 
          onClick={() => { setEditingForm({ 
            title: 'Your Sales Page',
            themeColor: '#10b981', 
            sections: [
               { id: 'sec-1', type: 'HEADER', label: 'Magira Pure Ginger', content: 'Grab your 500ml immunity booster today.' },
               { id: 'sec-2', type: 'BENEFITS', label: 'Why Magira?', content: 'Organic Ginger\nNo Sugar\nLocal Delivery' },
               { id: 'sec-3', type: 'PRODUCTS', label: 'Pick Your Package' },
               { id: 'sec-5', type: 'CONTACT', label: 'Customer Info' },
               { id: 'sec-6', type: 'ADDRESS', label: 'Delivery Address' },
            ],
            submitButtonText: 'Secure My Order',
            successMessage: 'Order received. We will call you within 15 minutes to confirm.',
            thankYouUrl: '',
          }); setShowModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-xl font-black shadow-lg shadow-emerald-100 transition text-[11px] uppercase tracking-widest"
        >
          + Build New Page
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {forms.filter(f => user?.role === 'Admin' || f.createdBy === user?.name || f.assignedToNames?.includes(user?.name || '')).map(form => (
          <div key={form.id} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col group">
            <div className="h-3" style={{ backgroundColor: form.themeColor }}></div>
            <div className="p-8 flex-1 flex flex-col">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-black text-slate-800 text-lg leading-tight uppercase truncate">{form.title}</h3>
                {form.assignedToNames && form.assignedToNames.length > 0 && (
                  <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg uppercase tracking-widest">
                    Assigned to: {form.assignedToNames.length} Agents
                  </span>
                )}
              </div>
              
              <div className="mt-2 mb-8 flex flex-wrap gap-1.5">
                 {form.sections.slice(0, 4).map(s => (
                   <span key={s.id} className="text-[8px] font-black bg-slate-50 text-slate-400 px-2 py-0.5 rounded uppercase">{s.type}</span>
                 ))}
                 {form.sections.length > 4 && <span className="text-[8px] font-black bg-slate-50 text-slate-400 px-2 py-0.5 rounded uppercase">+{form.sections.length - 4}</span>}
              </div>

              <div className="grid grid-cols-1 gap-2 mt-auto">
                <button 
                  onClick={() => { setPreviewForm(form); setPreviewSubmitted(false); }}
                  className="bg-slate-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition shadow-lg shadow-slate-200"
                >
                  Live Preview
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => { setEditingForm(form); setShowModal(true); }}
                    className="bg-slate-50 hover:bg-slate-100 text-slate-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition"
                  >
                    Edit Layout
                  </button>
                  <button 
                    onClick={() => setShowCodeModal(form)}
                    className="bg-emerald-50 hover:bg-emerald-100 text-emerald-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition"
                  >
                    Get Link
                  </button>
                </div>
                {user?.role === 'Admin' && (
                  <button 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this form? This cannot be undone.')) {
                        await db.deleteForm(form.id);
                      }
                    }}
                    className="w-full bg-red-50 hover:bg-red-100 text-red-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition border border-red-100"
                  >
                    Delete Form
                  </button>
                )}
                {user?.role === 'Admin' && (
                  <div className="mt-2 border-t border-slate-50 pt-2">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(form.assignedToNames || []).map(name => (
                        <span key={name} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[8px] font-black uppercase flex items-center gap-1">
                          {name}
                          <button 
                            onClick={async () => {
                              const updatedNames = (form.assignedToNames || []).filter(n => n !== name);
                              await db.saveForm({ ...form, assignedToNames: updatedNames });
                            }}
                            className="hover:text-red-500"
                          >✕</button>
                        </span>
                      ))}
                    </div>
                    <select 
                      className="w-full bg-slate-50 border-none rounded-xl px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 focus:ring-1 focus:ring-emerald-500"
                      value=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        const currentNames = form.assignedToNames || [];
                        if (!currentNames.includes(e.target.value)) {
                          const updatedForm = { ...form, assignedToNames: [...currentNames, e.target.value] };
                          await db.saveForm(updatedForm);
                        }
                      }}
                    >
                      <option value="">+ Add Agent...</option>
                      {users.map(u => (
                        <option key={u.id} value={u.name}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCodeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-[3rem] w-full max-w-2xl shadow-2xl overflow-hidden">
            <div className="p-10 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Lead Recovery Script</h2>
              <button onClick={() => setShowCodeModal(null)} className="text-slate-400 hover:text-slate-600 bg-white w-8 h-8 rounded-full flex items-center justify-center shadow-sm">✕</button>
            </div>
            <div className="p-10 space-y-6">
              <div className="bg-amber-50 p-6 rounded-[1.5rem] border border-amber-100">
                 <p className="text-[11px] text-amber-800 font-black uppercase tracking-widest mb-2">Live Field Capture Enabled</p>
                 <p className="text-xs text-amber-900 font-medium leading-relaxed">This script now tracks every input in real-time. If a user leaves without clicking submit, their name and phone will appear in your "Lost Carts" dashboard for recovery calls.</p>
              </div>
              <pre className="bg-slate-900 text-emerald-400 p-8 rounded-[2rem] text-[10px] overflow-x-auto max-h-[300px] leading-relaxed font-mono relative group border-4 border-slate-800">
                {getEmbedCode(showCodeModal)}
              </pre>
              <button 
                onClick={() => {
                   navigator.clipboard.writeText(getEmbedCode(showCodeModal));
                   alert('Embed Script with recovery logic copied!');
                }}
                className="w-full bg-emerald-600 text-white py-5 rounded-[1.5rem] font-black uppercase tracking-widest hover:bg-emerald-700 transition shadow-xl shadow-emerald-100"
              >
                Copy Recovery Script
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[2.5rem] w-full max-w-6xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-[90vh]">
            <div className="w-full md:w-80 bg-slate-50 border-r border-slate-100 p-8 overflow-y-auto">
              <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8">Design Toolbox</h2>
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Essentials</p>
                  <div className="grid grid-cols-1 gap-2">
                    {(['HEADER', 'IMAGE', 'CONTACT', 'PRODUCTS', 'ADDRESS', 'LOCATION', 'DELIVERY_INSTRUCTIONS', 'CUSTOM_TEXT', 'BENEFITS', 'TESTIMONIALS', 'FAQ'] as SectionType[]).map(type => (
                      <button key={type} onClick={() => addSection(type)} className="w-full bg-white border border-slate-200 p-3 rounded-xl text-left hover:border-emerald-500 transition-all flex items-center gap-3 group">
                        <span className="bg-slate-50 text-slate-400 group-hover:bg-emerald-50 group-hover:text-emerald-600 w-8 h-8 rounded-lg flex items-center justify-center text-xs">＋</span>
                        <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">{type.replace('_', ' ')}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-6 border-t border-slate-200">
                    <label className="text-[10px] font-black text-slate-400 uppercase mb-2 block">Brand Color</label>
                    <input type="color" className="w-full h-10 border-none rounded-xl cursor-pointer" value={editingForm?.themeColor} onChange={e => setEditingForm({...editingForm, themeColor: e.target.value})} />
                </div>

                {user?.role === 'Admin' && (
                  <div className="pt-6 border-t border-slate-200">
                    <label className="text-[10px] font-black text-slate-400 uppercase mb-2 block tracking-widest">Assigned Agents</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(editingForm?.assignedToNames || []).map(name => (
                        <span key={name} className="bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase flex items-center gap-2">
                          {name}
                          <button 
                            onClick={() => {
                              const updated = (editingForm?.assignedToNames || []).filter(n => n !== name);
                              setEditingForm({...editingForm, assignedToNames: updated});
                            }}
                            className="hover:text-red-500"
                          >✕</button>
                        </span>
                      ))}
                    </div>
                    <select 
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                      value=""
                      onChange={e => {
                        if (!e.target.value) return;
                        const current = editingForm?.assignedToNames || [];
                        if (!current.includes(e.target.value)) {
                          setEditingForm({...editingForm, assignedToNames: [...current, e.target.value]});
                        }
                      }}
                    >
                      <option value="">-- Add Agent --</option>
                      {users.map(u => (
                        <option key={u.id} value={u.name}>{u.name} ({u.role})</option>
                      ))}
                    </select>
                    <p className="text-[9px] text-slate-400 mt-2 leading-relaxed">Assigned agents can view this form and its leads in their dashboard.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col bg-slate-100/30 p-8 overflow-y-auto">
               <div className="max-w-2xl mx-auto w-full space-y-6">
                  <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                     <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Campaign Identity</label>
                        <input 
                            className="w-full text-2xl font-black border-none p-0 focus:ring-0 placeholder-slate-200 uppercase"
                            placeholder="Page Name..."
                            value={editingForm?.title || ''}
                            onChange={e => setEditingForm({...editingForm, title: e.target.value})}
                        />
                        <textarea 
                            className="w-full text-sm font-medium border-none p-0 focus:ring-0 placeholder-slate-300 mt-2 resize-none"
                            placeholder="Brief description of this campaign..."
                            rows={2}
                            value={editingForm?.description || ''}
                            onChange={e => setEditingForm({...editingForm, description: e.target.value})}
                        />
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-50">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Submit Button Text</label>
                            <input 
                                className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                placeholder="e.g. Place Order Now"
                                value={editingForm?.submitButtonText || ''}
                                onChange={e => setEditingForm({...editingForm, submitButtonText: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Thank You Redirect URL (Optional)</label>
                            <input 
                                className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                placeholder="https://..."
                                value={editingForm?.thankYouUrl || ''}
                                onChange={e => setEditingForm({...editingForm, thankYouUrl: e.target.value})}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Success Message</label>
                            <textarea 
                                className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                placeholder="What users see after submitting..."
                                rows={2}
                                value={editingForm?.successMessage || ''}
                                onChange={e => setEditingForm({...editingForm, successMessage: e.target.value})}
                            />
                        </div>
                     </div>
                  </div>
                  <div className="space-y-4">
                    {(editingForm?.sections || []).map((section, idx) => (
                      <div key={section.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm group">
                        <div className="flex items-center justify-between mb-4">
                           <div className="flex items-center gap-2">
                              <span className="text-[9px] font-black bg-slate-900 text-white px-2 py-0.5 rounded uppercase">{section.type}</span>
                              <input 
                                className="text-xs font-black border-none p-0 focus:ring-0 bg-transparent w-48 uppercase tracking-widest"
                                value={section.label || ''}
                                onChange={e => {
                                  const updated = [...(editingForm?.sections || [])];
                                  updated[idx].label = e.target.value;
                                  setEditingForm({...editingForm, sections: updated});
                                }}
                              />
                           </div>
                           <div className="flex gap-1">
                              <button onClick={() => moveSection(idx, 'up')} className="p-1.5 hover:bg-slate-100 rounded text-slate-400">↑</button>
                              <button onClick={() => moveSection(idx, 'down')} className="p-1.5 hover:bg-slate-100 rounded text-slate-400">↓</button>
                              <button onClick={() => removeSection(section.id)} className="p-1.5 hover:bg-red-50 rounded text-red-400 ml-2">✕</button>
                           </div>
                        </div>
                        {['HEADER', 'BENEFITS', 'TESTIMONIALS', 'IMAGE', 'FAQ', 'CUSTOM_TEXT', 'DELIVERY_INSTRUCTIONS', 'ADDRESS'].includes(section.type) && (
                          <textarea className="w-full text-sm bg-slate-50 border-none rounded-xl p-4 focus:ring-2 focus:ring-emerald-500 font-medium" rows={2} value={section.content || ''} onChange={e => {
                            const updated = [...(editingForm?.sections || [])];
                            updated[idx].content = e.target.value;
                            setEditingForm({...editingForm, sections: updated});
                          }} placeholder={`Enter ${section.type.toLowerCase()} content...`}/>
                        )}

                        {section.type === 'PRODUCTS' && (
                          <div className="space-y-3 mt-4">
                            <div className="flex justify-between items-center">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Package Options</label>
                              <button onClick={() => addOption(idx)} className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-widest">+ Add Package</button>
                            </div>
                            <div className="space-y-2">
                              {(section.options || []).map((opt, optIdx) => (
                                <div key={optIdx} className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
                                  <input 
                                    className="flex-1 bg-transparent border-none text-xs font-bold focus:ring-0 p-0"
                                    value={opt.label}
                                    onChange={e => updateOption(idx, optIdx, 'label', e.target.value)}
                                    placeholder="Package Label"
                                  />
                                  <div className="flex items-center gap-2 border-l border-slate-200 pl-2">
                                    <span className="text-[9px] font-black text-slate-400 uppercase">Qty</span>
                                    <input 
                                      type="number"
                                      className="w-12 bg-transparent border-none text-xs font-bold focus:ring-0 p-0 text-center"
                                      value={opt.qty || 0}
                                      onChange={e => updateOption(idx, optIdx, 'qty', e.target.value)}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2 border-l border-slate-200 pl-2">
                                    <span className="text-[9px] font-black text-slate-400 uppercase">₦</span>
                                    <input 
                                      type="number"
                                      className="w-20 bg-transparent border-none text-xs font-bold focus:ring-0 p-0"
                                      value={opt.price || 0}
                                      onChange={e => updateOption(idx, optIdx, 'price', e.target.value)}
                                    />
                                  </div>
                                  <button onClick={() => removeOption(idx, optIdx)} className="text-slate-300 hover:text-red-500 transition">✕</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-12 pb-20">
                     <button onClick={() => handleSave()} className="bg-emerald-600 text-white px-10 py-5 rounded-[1.5rem] font-black shadow-xl shadow-emerald-100 uppercase tracking-widest text-[11px]">Deploy Page</button>
                  </div>
               </div>
            </div>
          </div>
        </div>
      )}

      {previewForm && (
        <div className="fixed inset-0 bg-slate-900/98 backdrop-blur-2xl flex flex-col z-[100] animate-in fade-in duration-300">
           <div className="h-20 bg-black/50 border-b border-white/5 flex items-center justify-between px-10 shrink-0">
              <button onClick={() => setPreviewForm(null)} className="text-white/60 hover:text-white transition font-black text-[10px] uppercase tracking-[0.3em]">← Exit Preview</button>
              <div className="flex items-center bg-white/5 p-1 rounded-2xl">
                 <button onClick={() => setPreviewDevice('desktop')} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${previewDevice === 'desktop' ? 'bg-white text-slate-900 shadow-xl' : 'text-white/40'}`}>Desktop</button>
                 <button onClick={() => setPreviewDevice('mobile')} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${previewDevice === 'mobile' ? 'bg-white text-slate-900 shadow-xl' : 'text-white/40'}`}>Mobile</button>
              </div>
              <div className="text-emerald-400 text-[10px] font-black uppercase tracking-widest">Interactive Testing Enabled</div>
           </div>

           <div className="flex-1 overflow-hidden p-8 flex items-center justify-center">
              <div className={`transition-all duration-700 bg-white shadow-2xl flex flex-col ${previewDevice === 'mobile' ? 'w-[375px] h-[760px] rounded-[3.5rem] border-[14px] border-slate-800' : 'w-full max-w-4xl h-full rounded-[2.5rem]'}`}>
                <div className="flex-1 overflow-y-auto no-scrollbar relative bg-white">
                   {previewSubmitted ? (
                      <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                        <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-5xl mb-8 shadow-2xl shadow-emerald-100 animate-bounce">✅</div>
                        <h2 className="text-3xl font-black text-slate-800 mb-4 tracking-tight leading-tight">{previewForm.successMessage}</h2>
                      </div>
                   ) : (
                    <form onSubmit={handlePreviewSubmit} className="flex flex-col gap-0 pb-20">
                      {previewForm.sections.map(sec => {
                         switch(sec.type) {
                            case 'HEADER': return <div key={sec.id} style={{ backgroundColor: previewForm.themeColor }} className="p-12 text-white text-center"><h2 className="text-3xl font-black mb-4 tracking-tight leading-tight">{sec.label}</h2><p className="opacity-80 text-sm font-medium">{sec.content}</p></div>;
                            case 'IMAGE': return <div key={sec.id} className="w-full"><img src={sec.content} className="w-full h-auto" /></div>;
                            case 'CONTACT': return (
                              <div key={sec.id} className="px-10 py-8 space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{sec.label}</label>
                                <input required placeholder="Name" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none" value={previewData.customerName} onChange={e => setPreviewData({...previewData, customerName: e.target.value})} />
                                <input required type="tel" pattern="\\d{11}" title="Please enter exactly 11 digits" placeholder="Phone (11 Digits)" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none" value={previewData.phone} onChange={e => setPreviewData({...previewData, phone: e.target.value})} />
                                <input required type="tel" pattern="\\d{11}" title="Please enter exactly 11 digits" placeholder="WhatsApp (11 Digits)" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none" value={previewData.whatsapp} onChange={e => setPreviewData({...previewData, whatsapp: e.target.value})} />
                              </div>
                            );
                            case 'PRODUCTS': 
                              const agentPackages = getPackagesForAgent(previewForm.assignedToNames?.[0] || previewForm.createdBy);
                              return (
                              <div key={sec.id} className="px-10 py-8 border-b border-slate-50">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">{sec.label}</label>
                                <select required className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-black appearance-none" value={previewData.package} onChange={e => setPreviewData({...previewData, package: e.target.value})}>
                                  <option value="">Choose Package...</option>
                                  {(sec.options || agentPackages.map(p => ({ label: p.label, value: JSON.stringify({ qty: p.qty, price: p.price, label: p.label }) }))).map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)}
                                </select>
                              </div>
                            );
                            case 'ADDRESS': return (
                              <div key={sec.id} className="px-10 py-8 border-b border-slate-50">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">{sec.label}</label>
                                <textarea required placeholder={sec.content || "Address..."} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-bold resize-none" rows={3} value={previewData.address} onChange={e => setPreviewData({...previewData, address: e.target.value})} />
                              </div>
                            );
                            case 'LOCATION': return (
                              <div key={sec.id} className="px-10 py-8 border-b border-slate-50">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">{sec.label}</label>
                                <select required className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-black appearance-none" value={previewData.stateName} onChange={e => setPreviewData({...previewData, stateName: e.target.value})}>
                                  <option value="">Select State...</option>
                                  {NIGERIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                            );
                            case 'DELIVERY_INSTRUCTIONS': return (
                              <div key={sec.id} className="px-10 py-8 border-b border-slate-50">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">{sec.label}</label>
                                <textarea required placeholder={sec.content} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 text-sm font-bold resize-none" rows={2} value={previewData.deliveryInstructions} onChange={e => setPreviewData({...previewData, deliveryInstructions: e.target.value})} />
                              </div>
                            );
                            case 'BENEFITS': return (
                              <div key={sec.id} className="px-10 py-8 bg-slate-50 mx-10 my-4 rounded-3xl">
                                <h3 className="text-sm font-black text-slate-800 mb-4 uppercase tracking-widest">{sec.label}</h3>
                                <ul className="space-y-3">
                                  {(sec.content || '').split('\n').map((b, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-600">
                                      <span style={{ color: previewForm.themeColor }}>✓</span> {b.trim()}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                            case 'TESTIMONIALS': return (
                              <div key={sec.id} className="px-10 py-8 border-2 border-slate-50 mx-10 my-4 rounded-3xl">
                                <p className="text-sm font-medium text-slate-600 italic leading-relaxed">"{sec.content}"</p>
                                <p className="text-xs font-black text-slate-800 mt-4 uppercase tracking-widest">— {sec.label}</p>
                              </div>
                            );
                            case 'FAQ': return (
                              <div key={sec.id} className="px-10 py-8">
                                <h3 className="text-sm font-black text-slate-800 mb-2 uppercase tracking-widest">Q: {sec.label}</h3>
                                <p className="text-sm font-medium text-slate-500 leading-relaxed">A: {sec.content}</p>
                              </div>
                            );
                            case 'CUSTOM_TEXT': return (
                              <div key={sec.id} className="px-10 py-8">
                                <h3 className="text-sm font-black text-slate-800 mb-2 uppercase tracking-widest">{sec.label}</h3>
                                <p className="text-sm font-medium text-slate-500 leading-relaxed">{sec.content}</p>
                              </div>
                            );
                            default: return null;
                         }
                      })}
                      <div className="px-10 mt-12">
                         <button type="submit" disabled={isSubmittingPreview} style={{ backgroundColor: previewForm.themeColor }} className={`w-full text-white py-6 rounded-[2rem] font-black uppercase tracking-[0.3em] text-sm shadow-2xl ${isSubmittingPreview ? 'opacity-70' : ''}`}>
                           {isSubmittingPreview ? 'Transmitting...' : previewForm.submitButtonText}
                         </button>
                      </div>
                    </form>
                   )}
                </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
